import { removeTrailingSlash } from './remove-trailing-slash';
import { replaceUmlauts } from './replace-umlauts';

const sanitizeUrl = (url: string) => {
  try {
    const decodedUrl = replaceUmlauts(decodeURIComponent(url));
    return new URL(replaceUmlauts(decodedUrl));
  } catch {
    throw new Error(`Either NEXT_PUBLIC_BASE_URL is not set, or invalid URL provided.`);
  }
};

/**
 * Returns a string that replaces the `defaultDomain` in `url` with `flavorDomain`.
 *
 * @param defaultDomain The domain to replace.
 * @param flavorDomain The flavor domain to use for replacement.
 * @param url The full URL.
 * @returns
 */
export const replaceDomain = (
  defaultDomain: string,
  flavorDomain = '',
  url = process.env.NEXT_PUBLIC_BASE_URL ?? 'http://localhost',
  isPraxia = false,
  removeQueryParams = false,
  shouldRemoveTrailingSlash = false,
  isJobDetailsPage = false,
) => {
  let finalUrl = url;

  /*
   * Job Details pages have special domain handling logic:
   *  - We will use the flavor domain (employer source domain) as canonical domain, overriding the NEXT_PUBLIC_BASE_URL setting.
   * The rest of the pages, for example LLPs, just need to check if the flavor is Praxia to switch the domain accordingly.
   */
  if (isJobDetailsPage) {
    if (flavorDomain === 'praxia.de') finalUrl = url.replace('pflegia', 'praxia');
    else finalUrl = url.replace('praxia', 'pflegia');
  } else if (isPraxia) {
    finalUrl = url.replace('pflegia', 'praxia');
  }

  const currentUrl = sanitizeUrl(finalUrl);

  const buildUrl = (host: string) => {
    const path = currentUrl.pathname === '/' ? '' : currentUrl.pathname.replace(/^\/+/, '/');
    const extraParams = `${currentUrl.search}${currentUrl.hash}`;
    const resultUrl = `${currentUrl.protocol}//${host}${path}${removeQueryParams ? '' : extraParams}`;
    return shouldRemoveTrailingSlash ? removeTrailingSlash(resultUrl) : resultUrl;
  };

  // Case 1: Replace default domain with flavor domain
  if (currentUrl.host.includes(defaultDomain)) {
    return buildUrl(flavorDomain ? currentUrl.host.replace(defaultDomain, flavorDomain) : currentUrl.host);
  }

  // Case 2: Normalize domains of the same base when defaultDomain === flavorDomain
  if (defaultDomain === flavorDomain) {
    const [baseDomain] = defaultDomain.split('.');

    if (currentUrl.host.includes(baseDomain) && currentUrl.host !== flavorDomain) {
      const hostParts = currentUrl.host.split('.');
      const domainPartsCount = 2;
      const hasSubdomain = hostParts.length > domainPartsCount;
      const subdomain = hasSubdomain ? `${hostParts.slice(0, -domainPartsCount).join('.')}.` : '';

      const newHost = `${subdomain}${flavorDomain}`;
      return buildUrl(newHost);
    }
  }

  // Case 3: No domain replacement needed, but apply removeQueryParams if required
  return buildUrl(currentUrl.host);
};
